<template>
    <Section class="checkout-contact">
        <template #left>
            <div class="checkout-contact-content">
                <Notification
                    class="checkout-contact-message"
                    variant="positive"
                    icon="Timer"
                    padding="l"
                    :tier="3"
                    :title="t({ key: 'checkout.progress_message' })" />

                <FormV2
                    id="form_contact"
                    :onValidChange="handleValidChange"
                    :initialValues="initialValues"
                    :validationSchema="validationSchema">
                    <div class="checkout-contact-form">
                        <div
                            v-if="initialValues.Agency && agency"
                            class="checkout-contact-section">
                            <p class="checkout-contact-title">
                                {{ t({ key: 'shared.agency_details' }) }}
                            </p>

                            <div class="checkout-contact-fields">
                                <TextboxField
                                    v-model="agency.AgencyNumber"
                                    name="Agency.AgencyNumber"
                                    :input="{
                                        label: t({
                                            key: 'shared.agency_number',
                                        }),
                                        readonly: true,
                                    }" />
                                <TextboxField
                                    v-model="agency.Reference"
                                    name="Agency.Reference"
                                    :input="{
                                        label: t({
                                            key: 'shared.your_reference',
                                        }),
                                    }" />
                                <TextboxField
                                    v-model="agency.Email"
                                    name="Agency.Email"
                                    :input="{
                                        label: t({
                                            key: 'checkout.email_label',
                                        }),
                                        type: 'email',
                                        autocomplete: 'email',
                                    }" />
                                <PhoneField
                                    v-model="agency.Phone"
                                    name="Agency.Phone"
                                    :input="{
                                        countryCode,
                                        label: t({
                                            key: 'shared.phone_number',
                                        }),
                                    }" />
                            </div>
                        </div>

                        <div class="checkout-contact-section">
                            <p class="checkout-contact-title">
                                {{
                                    t({
                                        key: agency
                                            ? 'shared.customer_details'
                                            : 'shared.contact_details',
                                    })
                                }}
                            </p>

                            <div class="checkout-contact-fields">
                                <TextboxField
                                    v-model="customer.Email"
                                    name="Customer.Email"
                                    :onBlurSuccess="handleEmailCompleted"
                                    :input="{
                                        label: t({
                                            key: 'checkout.email_label',
                                        }),
                                        hint: t({
                                            key: 'checkout.confirmation_sent_address',
                                        }),
                                        type: 'email',
                                        autocomplete: 'email',
                                    }" />
                                <PhoneField
                                    v-model="customer.PhoneNumber"
                                    v-model:countryCode="customer.CountryCode"
                                    name="Customer.PhoneNumber"
                                    :input="{
                                        countryCode,
                                        label: t({
                                            key: 'shared.phone_number',
                                        }),
                                    }" />
                            </div>
                        </div>
                    </div>

                    <div class="checkout-contact-section">
                        <Checkbox
                            v-if="!agency"
                            v-model="optoutNewsletter"
                            class="checkout-contact-newsletter">
                            {{
                                t({
                                    key: 'checkout.email_offers_label',
                                })
                            }}
                        </Checkbox>
                    </div>
                </FormV2>

                <div v-if="!isLargeDesktop" class="checkout-contact-content-usp">
                    <Usp v-if="!isStena" :hasLeaBank="isLeaBankAvailable" :market="market" />
                </div>
            </div>
        </template>

        <template v-if="isLargeDesktop" #right>
            <Usp v-if="!isStena" :hasLeaBank="isLeaBankAvailable" :market="market" />
        </template>
    </Section>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { useStore } from '@/store';
    import { defineComponent, computed, ref, watch } from 'vue';
    import { Agency, Customer } from '@/api/Cart/cart.interfaces';
    import { ValidationSchema } from '@ui/components/FormV2/Validations/Validations.interfaces';
    import { Market } from '@shared/config/market/market.models';
    import { isStenaCompany } from '@shared/config/market/market.helpers';
    import {
        isRequired,
        isEmail,
        isPhoneNumber,
    } from '@ui/components/FormV2/Validations/functions';
    import {
        logStatsigCustomerDetailsInitiated,
        logStatsigCustomerOptOutNewsletter,
    } from '@/tracking/statsig/checkout.functions';
    import { useScreenSize } from '@shared/helpers/viewport';
    import {
        logAnalyticsCustomerDetailsInitiated,
        logAnalyticsCustomerEmailCompleted,
    } from '@/tracking/analytics/Checkout/Functions/checkout.functions';
    import { Money } from '@libs/money/money.interfaces';
    import { semboSwedishCompanyData } from '@shared/config/market/data/market.data.sembo';
    import Usp from '../../Shared/Usp/Usp.vue';
    import Section from '../../Shared/Section/Section.vue';
    import FormV2 from '@ui/components/FormV2/Form.vue';
    import PhoneField from '@ui/components/FormV2/Fields/Phone/PhoneField.vue';
    import TextboxField from '@ui/components/FormV2/Fields/Textbox/TextboxField.vue';
    import Checkbox from '@ui/components/FormV2/Fields/Checkbox/Checkbox.vue';
    import Notification from '@ui/components/Notification/Notification.vue';

    export default defineComponent({
        name: 'Contact',
        methods: { t },
        components: {
            Section,
            Usp,
            FormV2,
            PhoneField,
            TextboxField,
            Checkbox,
            Notification,
        },
        props: { sectionId: String },
        setup(props) {
            const store = useStore();
            const { isLargeDesktop } = useScreenSize();

            const market = computed((): Market => store.getters['general/getMarket']);

            const totalToPay = computed<Money>(() => store.getters['cart/getTotalToPay']);

            const estimatedMargin = computed<Money>(
                () => store.getters['cart/getEstimatedMargin']
            );

            const isStena = computed(() => isStenaCompany(market.value?.number));

            const hasFlightProducts = computed(() => store.getters['cart/hasFlightProducts']);

            const isLeaBankAvailable = computed(
                () =>
                    isLeabankAvailableMarket(market.value.number) && !hasFlightProducts.value
            );

            const customer = computed<Customer>(() => store.getters['cart/getCustomer']);

            const agency = computed<Agency | null>(() => store.getters['cart/getAgency']);

            const optoutNewsletter = ref<boolean>(!customer.value.ReceiveNewsletter);

            const countryCode = computed((): string | undefined => {
                const customerCountryCode =
                    customer.value.CountryCode &&
                    customer.value.CountryCode !== '' &&
                    customer.value.CountryCode !== '0';

                if (customerCountryCode) {
                    return customer.value.CountryCode;
                }

                if (market.value) {
                    return market.value?.fullLocale.split('-')[1];
                }

                return undefined;
            });

            const initialValues = {
                ...(agency.value && {
                    Agency: {
                        Email: agency.value.Email,
                        Phone: agency.value.Phone,
                        AgencyNumber: agency.value.AgencyNumber,
                        Reference: agency.value.Reference,
                    },
                }),
                Customer: {
                    PhoneNumber: customer.value.PhoneNumber,
                    Email: customer.value.Email,
                    CountryCode: customer.value.CountryCode,
                },
            };

            const validationSchema: ValidationSchema = {
                ...(agency.value && {
                    'Agency.Email': [isRequired, isEmail],
                    'Agency.Phone': [isPhoneNumber],
                    'Agency.AgencyNumber': [isRequired],
                }),
                'Customer.Email': [isRequired, isEmail],
                'Customer.PhoneNumber': [isPhoneNumber],
            };

            function handleValidChange(valid: boolean) {
                store.dispatch('checkout/setValid', {
                    id: props.sectionId,
                    valid,
                });
            }

            function handleEmailCompleted() {
                logAnalyticsCustomerEmailCompleted(totalToPay.value, estimatedMargin.value);
            }

            function isLeabankAvailableMarket(marketNumber: number): boolean {
                return [semboSwedishCompanyData.number].includes(marketNumber);
            }

            watch(
                customer,
                (newCustomer) => {
                    store.dispatch('cart/updateCustomerState', newCustomer);
                    if (newCustomer.Email || newCustomer.PhoneNumber) {
                        logAnalyticsCustomerDetailsInitiated(
                            totalToPay.value,
                            estimatedMargin.value
                        );
                        logStatsigCustomerDetailsInitiated(totalToPay.value);

                        if (isEmail(newCustomer.Email) === true) {
                            handleEmailCompleted();
                        }
                    }
                },
                { deep: true, immediate: true }
            );

            watch(
                optoutNewsletter,
                (value) => {
                    customer.value.ReceiveNewsletter = !value;

                    // TODO: Move this to "Update cart and options" to be sure since user can toggle on and off multiple times
                    if (!customer.value.ReceiveNewsletter) {
                        logStatsigCustomerOptOutNewsletter();
                    }
                },
                { immediate: true }
            );

            return {
                isStena,
                isLargeDesktop,
                agency,
                customer,
                market,
                countryCode,
                handleValidChange,
                handleEmailCompleted,
                initialValues,
                validationSchema,
                optoutNewsletter,
                isLeaBankAvailable,
            };
        },
    });
</script>

<style lang="scss">
    @forward 'Contact.scss';
</style>
